/**
 * Amplitude Custom Events - Centralized tracking utility
 * Uses CDN-loaded Amplitude SDK via window.amplitude
 */

declare global {
  interface Window {
    amplitude?: {
      init: (apiKey: string, options?: Record<string, any>) => void;
      track: (eventName: string, properties?: Record<string, any>) => void;
      setUserId: (userId: string) => void;
      identify: (...args: any[]) => void;
      flush: () => void;
      setGroup: (groupType: string, groupName: string | string[]) => void;
      revenue: (revenue: any) => void;
      getDeviceId: () => string;
      getSessionId: () => number;
      reset: () => void;
    };
  }
}

const track = (eventName: string, properties?: Record<string, any>) => {
  try {
    if (window.amplitude) {
      window.amplitude.track(eventName, properties);
      console.log('[Amplitude]', eventName, properties);
    } else {
      console.warn('[Amplitude] SDK not loaded, skipping:', eventName);
    }
  } catch (e) {
    console.warn('[Amplitude] Failed to track:', eventName, e);
  }
};

export const setAmplitudeUserId = (userId: string) => {
  try {
    if (window.amplitude) {
      window.amplitude.setUserId(userId);
    }
  } catch (e) {
    console.warn('[Amplitude] Failed to set user ID:', e);
  }
};

// ─── EVENT 1: Account Created ─────────────────────────────────────────
export const trackAccountCreated = (props: {
  method: string;
  account_type?: string; // 'owner' | 'member'
  company_id?: string;
  referrer?: string;
  utm_source?: string;
  utm_medium?: string;
  utm_campaign?: string;
}) => track('Account Created', props);

// ─── EVENT 2: Company Created ─────────────────────────────────────────
export const trackCompanyCreated = (props: {
  company_id: string;
  company_name?: string;
  owner_email?: string;
  company_size?: string;
}) => track('Company Created', props);

// ─── EVENT 3: Trial Started ──────────────────────────────────────────
export const trackTrialStarted = (props: {
  company_id: string;
  company_name?: string;
  owner_email?: string;
  trial_ends_at?: string;
  started_at?: string;
}) => track('Trial Started', props);

// ─── EVENT 4: Subscription Started ──────────────────────────────────
export const trackSubscriptionStarted = (props: {
  company_id: string;
  plan: string;
  trial_duration_days?: number;
}) => track('Subscription Started', props);

// ─── EVENT 5: Member Invited ─────────────────────────────────────────
export const trackMemberInvited = (props: {
  company_id: string;
  company_name?: string;
  role_invited: string;
  invited_by: string;
  invited_email: string;
}) => track('Member Invited', props);

// ─── EVENT 5.5: Invitation Accepted (link clicked) ──────────────────
export const trackInvitationAccepted = (props: {
  company_slug: string;
  invitation_id?: string;
  source_page: 'login' | 'signup_invitation';
}) => track('Invitation Accepted', props);

// ─── EVENT 6: Member Joined ─────────────────────────────────────────
export const trackMemberJoined = (props: {
  company_id: string;
  role: string;
  invitation_id?: string;
}) => track('Member Joined', props);

// ─── EVENT 7: Assessment Started ────────────────────────────────────
export const trackAssessmentStarted = (props: {
  assessment_type: string;
  company_id?: string | null;
  user_id?: string | null;
  user_email?: string | null;
  user_name?: string | null;
  company_name?: string | null;
  position_name?: string | null;
  assessment_id?: string;
}) => track('Assessment Started', props);

// ─── EVENT 8: Assessment Step Completed ─────────────────────────────
export const trackAssessmentStepCompleted = (props: {
  assessment_type: string;
  assessment_id?: string;
  step_number: number;
  total_steps: number;
  company_id?: string | null;
  user_id?: string | null;
  user_email?: string | null;
  user_name?: string | null;
}) => track('Assessment Step Completed', props);

// ─── EVENT 9: Assessment Completed ──────────────────────────────────
export const trackAssessmentCompleted = (props: {
  assessment_type: string;
  assessment_id?: string;
  company_id?: string | null;
  user_id?: string | null;
  user_email?: string | null;
  user_name?: string | null;
  company_name?: string | null;
  position_name?: string | null;
  duration_minutes?: number;
  total_steps_completed?: number;
}) => track('Assessment Completed', props);

// ─── EVENT 10: Assessment Abandoned ─────────────────────────────────
export const trackAssessmentAbandoned = (props: {
  assessment_type: string;
  assessment_id?: string;
  company_id?: string | null;
  user_id?: string | null;
  last_step_number: number;
  total_steps: number;
  percent_completed: number;
}) => track('Assessment Abandoned', props);

// ─── EVENT 11: Results Viewed ───────────────────────────────────────
export const trackResultsViewed = (props: {
  assessment_type: string;
  results_id: string;
  company_id?: string | null;
  viewed_by_user_id?: string | null;
  is_own_result: boolean;
  user_email?: string | null;
  user_name?: string | null;
  profile_name?: string | null;
  disc_code?: string | null;
  cognitive_percentile?: number | null;
}) => track('Results Viewed', props);

// ─── EVENT 12: Position Created ─────────────────────────────────────
export const trackPositionCreated = (props: {
  company_id?: string | null;
  company_name?: string | null;
  position_name: string;
  position_id: string;
  profile_source: string;
  selected_profile?: string | null;
  created_by?: string | null;
}) => track('Position Created', props);

// ─── EVENT 13: Ideal Profile Created ────────────────────────────────
export const trackIdealProfileCreated = (props: {
  company_id?: string | null;
  company_name?: string | null;
  profile_id?: string;
  profile_name?: string;
  dominance_target?: number;
  extraversion_target?: number;
  patience_target?: number;
  formality_target?: number;
  objectivity_target?: number;
  created_by?: string | null;
}) => track('Ideal Profile Created', props);

// ─── EVENT 14: Profile Comparison Triggered ─────────────────────────
export const trackProfileComparisonTriggered = (props: {
  company_id?: string | null;
  user_id?: string | null;
  person1_name: string;
  person1_email: string;
  person1_profile?: string | null;
  person2_name: string;
  person2_email: string;
  person2_profile?: string | null;
  compatibility_score?: number | null;
}) => track('Profile Comparison Triggered', props);

// ─── EVENT 15: Org Chart Viewed ─────────────────────────────────────
export const trackOrgChartViewed = (props: {
  company_id?: string | null;
  user_id?: string | null;
}) => track('Org Chart Viewed', props);

// ─── EVENT 16: Dashboard Viewed ─────────────────────────────────────
export const trackDashboardViewed = (props: {
  company_id?: string | null;
  user_id?: string | null;
  user_role?: string | null;
}) => track('Dashboard Viewed', props);

// ─── EVENT 17: My Results Viewed ────────────────────────────────────
export const trackMyResultsViewed = (props: {
  company_id?: string | null;
  user_id?: string | null;
  assessments_completed_count: number;
}) => track('My Results Viewed', props);

// ─── EVENT 18: Training Accessed ────────────────────────────────────
export const trackTrainingAccessed = (props: {
  company_id?: string | null;
  user_id?: string | null;
  training_topic?: string;
}) => track('Training Accessed', props);

// ─── EVENT 19: Work Behavior Viewed ─────────────────────────────────
export const trackWorkBehaviorViewed = (props: {
  company_id?: string | null;
  user_id?: string | null;
}) => track('Work Behavior Viewed', props);

// ─── EVENT 20: Subscription Cancelled ───────────────────────────────
export const trackSubscriptionCancelled = (props: {
  company_id: string;
  previous_plan: string;
  tenure_days?: number;
  total_assessments_completed?: number;
}) => track('Subscription Cancelled', props);


// ─── EVENT 21: Checkout Started ──────────────────────────────────────
export const trackCheckoutStarted = (props: {
  company_id?: string | null;
  company_name?: string | null;
  user_id?: string | null;
  user_email?: string | null;
  user_name?: string | null;
  current_plan?: string | null;
  target_plan: string;
  trial_days_remaining?: number | null;
  source: 'pricing_page' | 'business_page' | 'billing_settings';
}) => track('Checkout Started', props);

// ─── EVENT 22: Candidate Registered ─────────────────────────────────
export const trackCandidateRegistered = (props: {
  email: string;
  full_name: string;
  assessment_type: string;
  company_id?: string | null;
}) => track('Candidate Registered', props);

// ─── Standard Amplitude: Form Submitted ─────────────────────────────
export const trackFormSubmitted = (props: {
  form_name: string;
  success: boolean;
  error_message?: string;
}) => track('[Amplitude] Form Submitted', props);

// ─── Assessment Abandonment Detection ───────────────────────────────
const ABANDONMENT_THRESHOLD_MS = 5 * 60 * 1000; // 5 minutes

export const saveAssessmentProgress = (data: {
  assessment_type: string;
  assessment_id?: string;
  company_id?: string | null;
  user_id?: string | null;
  current_step: number;
  total_steps: number;
  started_at: number;
}) => {
  try {
    localStorage.setItem('amplitude_assessment_progress', JSON.stringify({
      ...data,
      last_heartbeat: Date.now(),
    }));
  } catch (e) {
    // Silently fail
  }
};

export const clearAssessmentProgress = () => {
  try {
    localStorage.removeItem('amplitude_assessment_progress');
  } catch (e) {
    // Silently fail
  }
};

/**
 * Fires Assessment Abandoned if a stale heartbeat (>5min) exists in localStorage.
 * Called on app mount (fallback) AND via visibilitychange (primary).
 */
export const checkForAbandonedAssessment = () => {
  try {
    const raw = localStorage.getItem('amplitude_assessment_progress');
    if (!raw) return;

    const data = JSON.parse(raw);
    const timeSinceHeartbeat = Date.now() - (data.last_heartbeat || 0);
    
    if (timeSinceHeartbeat > ABANDONMENT_THRESHOLD_MS) {
      const percentCompleted = data.total_steps > 0
        ? Math.round((data.current_step / data.total_steps) * 100)
        : 0;

      trackAssessmentAbandoned({
        assessment_type: data.assessment_type,
        assessment_id: data.assessment_id,
        company_id: data.company_id,
        user_id: data.user_id,
        last_step_number: data.current_step,
        total_steps: data.total_steps,
        percent_completed: percentCompleted,
      });

      clearAssessmentProgress();
    }
  } catch (e) {
    clearAssessmentProgress();
  }
};

/**
 * Sets up a visibilitychange listener that fires Assessment Abandoned
 * when the user leaves the tab (hidden) and the heartbeat is stale.
 * Uses sendBeacon for reliability on tab close.
 * Returns a cleanup function for useEffect.
 */
export const setupAbandonmentListener = (): (() => void) => {
  const handleVisibilityChange = () => {
    if (document.visibilityState === 'hidden') {
      // Check immediately — if heartbeat is already stale, fire now
      checkForAbandonedAssessment();
      
      // Also schedule a deferred check: mark the current time so that
      // when the tab becomes visible again (or on next mount), we can
      // detect if the user was away for > threshold
      try {
        const raw = localStorage.getItem('amplitude_assessment_progress');
        if (raw) {
          const data = JSON.parse(raw);
          // Update heartbeat to "now" so the threshold starts from tab-hide
          localStorage.setItem('amplitude_assessment_progress', JSON.stringify({
            ...data,
            last_heartbeat: Date.now(),
          }));
        }
      } catch (e) {
        // Silently fail
      }
    }

    if (document.visibilityState === 'visible') {
      // User returned — check if they were away longer than threshold
      checkForAbandonedAssessment();
    }
  };

  document.addEventListener('visibilitychange', handleVisibilityChange);

  return () => {
    document.removeEventListener('visibilitychange', handleVisibilityChange);
  };
};
